import { ARRAY_ERROR, getIn, setIn } from 'final-form';
import { useMemo } from 'react';
import { AnyObjectSchema, ValidationError, getIn as getSchemaIn } from 'yup';

/**
 * Sets the `innerError.message` in an `errors` object at the key
 * defined by `innerError.path`.
 */
const setInError = (errors: Record<string, string>, innerError: ValidationError) => {
  return setIn(errors, innerError.path ?? '', innerError.message);
};

/**
 * Sets an error about a whole array (e.g. `min(1)`) the way final-form-arrays expects it: under
 * `ARRAY_ERROR` on an array that keeps its item errors. A plain string at an array path breaks
 * final-form when it later sets an item error inside it ("Cannot set a numeric property on an
 * object"), for example when a row is added while the previous async validation said the list
 * was empty.
 */
const setInArrayError = (errors: Record<string, string>, innerError: ValidationError) => {
  const path = innerError.path ?? '';
  const current = getIn(errors, path);
  const arrayError: any = Array.isArray(current) ? [...current] : [];
  arrayError[ARRAY_ERROR] = innerError.message;
  return setIn(errors, path, arrayError);
};

/**
 * Empty object map with no prototype. Used as default
 * value for reducing the `err.inner` array of errors
 * from a `yup~ValidationError`.
 */
const emptyObj: {} = Object.create(null);

/**
 * Takes a `yup` validation schema and returns a function that expects
 * a map of values to validate. If the validation passes, the function resolves to `undefined`
 * (signalling that the values are valid). If the validation doesn't pass, it resolves
 * to a map of invalid field names to errors.
 */

export function makeValidate<TValue = Record<string, unknown>>(
  schema: AnyObjectSchema
): (val: TValue) => Promise<undefined | Record<string, string>> {
  return async function validate(values: TValue) {
    try {
      await schema.validate(values, { abortEarly: false });
    } catch (err) {
      if (err instanceof ValidationError) {
        // errors about a list of rows (an array of objects), not about a list of values
        const isArrayError = (innerError: ValidationError) => {
          if (!innerError.path || !Array.isArray(getIn(values as any, innerError.path))) return false;
          try {
            // resolve conditions (`when`) against the parent value to see the actual item type
            const { schema: target, parent } = getSchemaIn(schema, innerError.path, values);
            const resolved: any = target.resolve({ value: getIn(values as any, innerError.path), parent });
            return resolved?.type === 'array' && resolved.innerType?.type === 'object';
          } catch {
            return false;
          }
        };
        // item errors first: setting them later would copy the array and drop its ARRAY_ERROR
        const itemErrors = err.inner.filter((innerError) => !isArrayError(innerError));
        const arrayErrors = err.inner.filter(isArrayError);
        return arrayErrors.reduce(setInArrayError, itemErrors.reduce(setInError, emptyObj));
      }
    }
  };
}

export function useValidationSchema(schema: AnyObjectSchema) {
  const validate = useMemo(() => makeValidate(schema), [schema]);
  return validate;
}
